"use client";

import { Spinner } from "@/components/ui/spinner";

import { useRef } from "react";

import {
  ProcessDefinition
} from "@/lib/camunda";
import { cn } from "@/lib/utils";
import { useAppRouter as useRouter } from '@/lib/navigation';
import { CamundaFormsService } from '@/lib/camunda';

interface ProcessItemProps {
  process: ProcessDefinition;
  isStarting: boolean;
  onStart: () => void;
}

export function ProcessItem({ process, isStarting, onStart }: ProcessItemProps) {
  const router = useRouter();
  const startRouteCache = useRef(new Map<string, string | null>());
  const prefetchedRoutes = useRef(new Set<string>());

  const getStartProcessRoute = async (): Promise<string | null> => {
    const cachedRoute = startRouteCache.current.get(process.id);
    if (cachedRoute !== undefined) {
      return cachedRoute;
    }

    const startForm = await CamundaFormsService.getStartForm(process.id);
    const route = startForm?.key
      ? `/task-list/start-process/${encodeURIComponent(process.id)}/${encodeURIComponent(startForm.key)}`
      : null;

    startRouteCache.current.set(process.id, route);
    return route;
  };

  const prefetchStartProcessRoute = async () => {
    if (isStarting) return;
    try {
      const route = await getStartProcessRoute();
      if (route && !prefetchedRoutes.current.has(route)) {
        prefetchedRoutes.current.add(route);
        void router.prefetch(route);
      }
    } catch {
    }
  };

  const handleStartProcess = async () => {
    try {
      console.log(`🔍 Checking start form for process: ${process.key} (ID: ${process.id})`);

      const url = await getStartProcessRoute();
      console.log(`📋 Start form route:`, url);

      if (url) {
        console.log(`🎯 Redirecting to form: ${url}`);
        router.push(url);
      } else {
        console.log(`⚡ No start form found, starting process directly`);
        onStart();
      }
    } catch (error) {
      console.error('Error checking start form:', error);
      onStart();
    }
  };

  return (
    <div
      className={cn(
        "border-b border-gray-100 last:border-b-0 cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:shadow-sm",
        isStarting && "opacity-50 cursor-not-allowed"
      )}
    >
      <button
        type="button"
        disabled={isStarting}
        aria-label={`Iniciar proceso ${process.name || process.key}`}
        className="flex w-full items-center justify-between px-1 py-3 text-left"
        onClick={() => { void handleStartProcess(); }}
        onMouseEnter={() => { void prefetchStartProcessRoute(); }}
        onFocus={() => { void prefetchStartProcessRoute(); }}
      >
        <span className="flex flex-1 items-center space-x-2">
          <span className="font-medium text-gray-900 flex-1">
            {process.name || process.key}
          </span>
          
          {isStarting && (
            <Spinner />
          )}
        </span>
      </button>
    </div>
  );
}
